import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../lib/api.js';

const PRIMARY_CATEGORIES = [
  'UAS',
  'eVTOL / Advanced Aircraft',
  'Propulsion & Power',
  'Airspace Management',
  'Defense',
  'Market & Finance',
];

function fmt(dateStr) {
  if (!dateStr) return 'never';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const EMPTY_FORM = { name: '', feedUrl: '', primaryCategory: '', categories: '' };

function formToPayload(form) {
  return {
    name: form.name.trim(),
    feedUrl: form.feedUrl.trim(),
    ...(form.primaryCategory ? { primaryCategory: form.primaryCategory } : {}),
    categories: form.categories
      ? form.categories.split(',').map((s) => s.trim()).filter(Boolean)
      : [],
  };
}

function StatusBadge({ status }) {
  const cls =
    status === 'active'
      ? 'bg-hxd-ok'
      : status === 'error'
        ? 'bg-hxd-err'
        : 'bg-hxd-muted';
  return (
    <span
      className={`${cls} text-white font-display text-[9px] font-bold uppercase tracking-display-sm px-1.5 py-0.5 rounded-[2px]`}
    >
      {status}
    </span>
  );
}

function SourceForm({ values, onChange, onSubmit, submitting, submitLabel, onCancel }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="hxd-label">Name</label>
        <input
          className="hxd-input"
          value={values.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="Aviation Week"
          required
        />
      </div>
      <div>
        <label className="hxd-label">Feed URL</label>
        <input
          className="hxd-input"
          type="url"
          value={values.feedUrl}
          onChange={(e) => onChange('feedUrl', e.target.value)}
          placeholder="https://example.com/rss"
          required
        />
      </div>
      <div>
        <label className="hxd-label">
          Primary Category{' '}
          <span className="font-normal normal-case tracking-normal text-hxd-muted">
            (optional)
          </span>
        </label>
        <select
          className="hxd-input"
          value={values.primaryCategory}
          onChange={(e) => onChange('primaryCategory', e.target.value)}
        >
          <option value="">— none —</option>
          {PRIMARY_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="hxd-label">
          Additional Categories{' '}
          <span className="font-normal normal-case tracking-normal text-hxd-muted">
            (comma-separated, optional)
          </span>
        </label>
        <input
          className="hxd-input"
          value={values.categories}
          onChange={(e) => onChange('categories', e.target.value)}
          placeholder="UAS, Defense"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          className="hxd-btn hxd-btn-primary hxd-btn-sm"
          disabled={submitting || !values.name.trim() || !values.feedUrl.trim()}
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="hxd-btn hxd-btn-ghost hxd-btn-sm" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default function SourceManager({ apiBase = '/sources', showLastError = true }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(apiBase);
      setSources(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError(null);
    try {
      await api.post(apiBase, formToPayload(addForm));
      setAddForm(EMPTY_FORM);
      setShowAdd(false);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (source) => {
    setEditTarget(source);
    setEditForm({
      name: source.name ?? '',
      feedUrl: source.feedUrl ?? '',
      primaryCategory: source.primaryCategory ?? '',
      categories: Array.isArray(source.categories) ? source.categories.join(', ') : '',
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditing(true);
    setError(null);
    try {
      await api.put(`${apiBase}/${editTarget._id}`, formToPayload(editForm));
      setEditTarget(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setEditing(false);
    }
  };

  const handlePauseResume = async (source) => {
    setError(null);
    try {
      const action = source.status === 'paused' ? 'resume' : 'pause';
      await api.post(`${apiBase}/${source._id}/${action}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (source) => {
    if (!window.confirm(`Delete "${source.name}"?`)) return;
    setError(null);
    try {
      await api.del(`${apiBase}/${source._id}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const headers = showLastError
    ? ['Name', 'Feed URL', 'Category', 'Status', 'Last Scraped', 'Last Error', 'Actions']
    : ['Name', 'Feed URL', 'Category', 'Status', 'Last Scraped', 'Actions'];

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-[14px] font-bold tracking-display-md uppercase text-hxd-ink">
            News Sources
          </h2>
          <p className="text-[13px] text-hxd-muted mt-1">
            Feeds the AI scans daily to build each edition.
          </p>
        </div>
        <button
          className="hxd-btn hxd-btn-ghost hxd-btn-sm flex-shrink-0 mt-0.5"
          onClick={() => setShowAdd((v) => !v)}
        >
          {showAdd ? '− Cancel' : '+ Add source'}
        </button>
      </div>

      {error && (
        <div className="mb-5 flex items-start justify-between gap-3 bg-red-50 border border-red-200 rounded p-3">
          <p className="text-hxd-err text-[13px]">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-hxd-muted hover:text-hxd-err text-lg leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {showAdd && (
        <div className="hxd-card mb-6">
          <div className="hxd-card-header">New Source</div>
          <SourceForm
            values={addForm}
            onChange={(field, val) => setAddForm((f) => ({ ...f, [field]: val }))}
            onSubmit={handleAdd}
            submitting={adding}
            submitLabel="Add source"
            onCancel={() => {
              setShowAdd(false);
              setAddForm(EMPTY_FORM);
            }}
          />
        </div>
      )}

      {loading ? (
        <p className="text-hxd-muted text-[13px]">Loading sources…</p>
      ) : sources.length === 0 ? (
        error ? null : (
          <div className="hxd-card">
            <p className="text-hxd-muted text-[13px]">
              No sources yet — use the form above to add one.
            </p>
          </div>
        )
      ) : (
        <div className="hxd-card p-0 overflow-x-auto">
          <table className="w-full text-[13px] min-w-[800px]">
            <thead>
              <tr className="border-b-2 border-hxd-orange bg-hxd-surface">
                {headers.map((h) => (
                  <th
                    key={h}
                    className="font-display text-[10px] font-bold tracking-display-md uppercase text-left px-4 py-3"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr
                  key={s._id}
                  className="border-b border-hxd-border last:border-0 hover:bg-hxd-surface transition-colors"
                >
                  <td className="px-4 py-3 font-semibold text-hxd-ink whitespace-nowrap">
                    {s.name}
                  </td>
                  <td className="px-4 py-3 text-hxd-muted max-w-[200px]">
                    <a
                      href={s.feedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-hxd-orange truncate block transition-colors"
                    >
                      {s.feedUrl}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-hxd-muted whitespace-nowrap">
                    {s.primaryCategory || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-4 py-3 text-hxd-muted whitespace-nowrap">
                    {fmt(s.lastScrapedAt)}
                  </td>
                  {showLastError && (
                    <td className="px-4 py-3 text-hxd-muted max-w-[180px]">
                      {s.lastError ? (
                        <span className="text-hxd-err text-[12px] line-clamp-2" title={s.lastError}>
                          {s.lastError}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                        onClick={() => handlePauseResume(s)}
                      >
                        {s.status === 'paused' ? 'Resume' : 'Pause'}
                      </button>
                      <button
                        className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                        onClick={() => openEdit(s)}
                      >
                        Edit
                      </button>
                      <button
                        className="hxd-btn hxd-btn-ghost hxd-btn-sm text-hxd-err border-transparent"
                        onClick={() => handleDelete(s)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded border border-hxd-border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="hxd-card-header">Edit — {editTarget.name}</div>
            <SourceForm
              values={editForm}
              onChange={(field, val) => setEditForm((f) => ({ ...f, [field]: val }))}
              onSubmit={handleEdit}
              submitting={editing}
              submitLabel="Save changes"
              onCancel={() => setEditTarget(null)}
            />
          </div>
        </div>
      )}
    </>
  );
}
